// src/components/subscription/SavedPaymentMethods.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// import api from '../../utils/api';
import axios from 'axios';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 5000,
});

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const SavedPaymentMethods = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [processingCard, setProcessingCard] = useState(false);
  const [cardError, setCardError] = useState(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      const response = await api.get('/subscriptions/payment-methods', {
        headers: { Authorization: `Bearer ${token}` },
      });

      // const response = await api.get('/memberships/payment-methods');
      setPaymentMethods(response.data);
    } catch (error) {
      toast.error('Failed to fetch payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessingCard(true);
    setCardError(null);

    try {
      const token = localStorage.getItem('ownerToken');

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (error) {
        throw error;
      }

      await api.post(
        '/subscriptions/payment-methods',
        {
          paymentMethodId: paymentMethod.id,
          makeDefault: paymentMethods.length === 0, // Make default if it's the first card
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Payment method added successfully');
      await fetchPaymentMethods();
      setShowAddCard(false);
      elements.getElement(CardElement).clear();
    } catch (error) {
      setCardError(error.message || 'Failed to add payment method');

      toast.error(error.message || 'Failed to add payment method');
    } finally {
      setProcessingCard(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      const token = localStorage.getItem('ownerToken');

      // await api.patch(`/memberships/payment-methods/${paymentMethodId}/default`);

      await api.patch(
        `/subscriptions/payment-methods/${paymentMethodId}/default`,
        {
          paymentMethodId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Default payment method updated');
      await fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to update default payment method');
    }
  };

  const handleRemove = async (paymentMethodId) => {
    try {
      const token = localStorage.getItem('ownerToken');

      await api.delete(`/subscriptions/payment-methods/${paymentMethodId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Payment method removed');
      await fetchPaymentMethods();
    } catch (error) {
      toast.error(
        error.response?.data?.error || 'Failed to remove payment method'
      );
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-8'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center'>
        <h2 className='text-xl font-semibold'>Payment Methods</h2>
        <button
          onClick={() => setShowAddCard(!showAddCard)}
          className='text-blue-500 hover:text-blue-700'
        >
          {showAddCard ? 'Cancel' : '+ Add New Card'}
        </button>
      </div>

      {showAddCard && (
        <div className='bg-white p-6 rounded-lg shadow mb-6'>
          <form onSubmit={handleAddCard}>
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Card Information
              </label>
              <div className='border rounded-md p-3'>
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
            </div>
            {cardError && (
              <div className='text-red-500 text-sm mb-4'>{cardError}</div>
            )}
            <button
              type='submit'
              disabled={!stripe || processingCard}
              className='w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50'
            >
              {processingCard ? 'Adding...' : 'Add Card'}
            </button>
          </form>
        </div>
      )}

      <div className='space-y-4'>
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            className='bg-white p-4 rounded-lg shadow flex justify-between items-center'
          >
            <div className='flex items-center space-x-4'>
              <div className='text-2xl text-gray-400'>
                {method.brand === 'visa' && '💳'}
                {method.brand === 'mastercard' && '💳'}
                {method.brand === 'amex' && '💳'}
              </div>
              <div>
                <p className='font-medium'>
                  •••• {method.last4}
                  {method.isDefault && (
                    <span className='ml-2 text-sm text-green-600'>Default</span>
                  )}
                </p>
                <p className='text-sm text-gray-500'>
                  Expires {method.expMonth.toString().padStart(2, '0')}/
                  {method.expYear.toString().slice(-2)}
                </p>
              </div>
            </div>

            <div className='flex space-x-4'>
              {!method.isDefault && (
                <button
                  onClick={() => handleSetDefault(method.id)}
                  className='text-blue-500 hover:text-blue-700 text-sm'
                >
                  Make Default
                </button>
              )}
              {(!method.isDefault || paymentMethods.length > 1) && (
                <button
                  onClick={() => handleRemove(method.id)}
                  className='text-red-500 hover:text-red-700 text-sm'
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}

        {paymentMethods.length === 0 && !showAddCard && (
          <div className='text-center py-8 text-gray-500'>
            No payment methods saved. Add a card to get started.
          </div>
        )}
      </div>
    </div>
  );
};

// const fetchPaymentMethods = async () => {
//     try {
//       const token = localStorage.getItem('ownerToken');

//       const response = await api.get('/subscriptions/payment-methods', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setPaymentMethods(response.data);
//     } catch (error) {
//       toast.error('Failed to fetch payment methods');
//     } finally {
//       setLoading(false);
//     }
//   };

// const SavedPaymentMethods = () => {
//   const [paymentMethods, setPaymentMethods] = useState([]);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     fetchPaymentMethods();
//   }, []);

//   const fetchPaymentMethods = async () => {
//     try {
//       const token = localStorage.getItem('ownerToken');

//       const response = await api.get('/subscriptions/payment-methods', {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setPaymentMethods(response.data);
//     } catch (error) {
//       toast.error('Failed to fetch payment methods');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleRemovePaymentMethod = async (paymentMethodId) => {
//     try {
//       const token = localStorage.getItem('ownerToken');

//       await api.delete(`/subscriptions/payment-methods/${paymentMethodId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setPaymentMethods((methods) =>
//         methods.filter((method) => method.id !== paymentMethodId)
//       );
//       toast.success('Payment method removed successfully');
//     } catch (error) {
//       toast.error('Failed to remove payment method');
//     }
//   };

//   if (loading) {
//     return <div className='animate-pulse'>Loading...</div>;
//   }

//   return (
//     <div className='space-y-4'>
//       <h2 className='text-lg font-medium'>Saved Payment Methods</h2>

//       {paymentMethods.length === 0 ? (
//         <p className='text-gray-500'>No saved payment methods</p>
//       ) : (
//         <div className='space-y-2'>
//           {paymentMethods.map((method) => (
//             <div
//               key={method.id}
//               className='flex items-center justify-between p-4 bg-white rounded-lg shadow'
//             >
//               <div className='flex items-center space-x-4'>
//                 {method.card.brand === 'visa' && (
//                   <img src='/visa-icon.svg' alt='Visa' className='h-8 w-8' />
//                 )}
//                 {/* Add other card brand icons */}
//                 <div>
//                   <p className='font-medium'>•••• {method.card.last4}</p>
//                   <p className='text-sm text-gray-500'>
//                     Expires {method.card.exp_month}/{method.card.exp_year}
//                   </p>
//                 </div>
//               </div>

//               <button
//                 onClick={() => handleRemovePaymentMethod(method.id)}
//                 className='text-red-600 hover:text-red-800'
//               >
//                 Remove
//               </button>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

export default SavedPaymentMethods;
